import { useEffect, useRef, useState } from 'react';

const BAR_COUNT = 24;

/**
 * Live waveform driven by the WebAudio API. Hooked to the same <audio>
 * element the player drives — when the audio plays we sample the analyser
 * each frame and update bar heights; when paused we relax to a flat line.
 *
 * Why not pre-render: we can't ship a precomputed waveform without writing
 * something to R2 alongside the audio, which is out-of-scope for the
 * frontend. The live FFT is "good enough" and feels responsive.
 */
export function Waveform({ audio, isPlaying }: { audio: HTMLAudioElement | null; isPlaying: boolean }) {
  const [bars, setBars] = useState<number[]>(() => new Array(BAR_COUNT).fill(0.1));
  const analyserRef = useRef<AnalyserNode | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!audio) return;
    if (ctxRef.current) return;

    let cleanup: (() => void) | undefined;
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      const source = ctx.createMediaElementSource(audio);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      ctxRef.current = ctx;
      sourceRef.current = source;
      analyserRef.current = analyser;
      cleanup = () => {
        try { source.disconnect(); } catch { /* noop */ }
        try { analyser.disconnect(); } catch { /* noop */ }
        try { void ctx.close(); } catch { /* noop */ }
      };
    } catch {
      // Some browsers block AudioContext until user gesture; fall back to idle.
    }
    return cleanup;
  }, [audio]);

  useEffect(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    if (!isPlaying) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setBars(new Array(BAR_COUNT).fill(0.1));
      return;
    }

    void ctxRef.current?.resume();

    const buffer = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(buffer);
      // Down-sample buffer.length (32) → BAR_COUNT (24)
      const next = new Array(BAR_COUNT).fill(0).map((_, i) => {
        const j = Math.floor((i / BAR_COUNT) * buffer.length);
        return Math.max(0.08, buffer[j] / 255);
      });
      setBars(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying]);

  return (
    <div className={`waveform ${isPlaying ? 'active' : 'idle'}`}>
      {bars.map((h, i) => (
        <div
          key={i}
          className="waveform-bar"
          style={{ height: `${4 + h * 20}px` }}
        />
      ))}
    </div>
  );
}
